/**
 * Central datetime formatting utilities for consistent time display across the app.
 * 
 * All database timestamps are stored in UTC.
 * Display times are shown in Europe/Stockholm (CET/CEST) with UTC in parentheses.
 */

/**
 * Format a date/time in Europe/Stockholm timezone with timezone abbreviation
 * @param date - Date string or Date object
 * @param opts - Optional formatting options
 * @returns Formatted string like "2025-11-13 13:48:18 CET"
 */
export function formatStockholm(
  date: string | Date | null | undefined,
  opts?: { 
    dateStyle?: 'full' | 'long' | 'medium' | 'short';
    timeStyle?: 'full' | 'long' | 'medium' | 'short';
    includeSeconds?: boolean;
  }
): string {
  if (!date) return '—';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '—';

  const defaultOpts: Intl.DateTimeFormatOptions = {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: opts?.includeSeconds !== false ? '2-digit' : undefined,
    timeZoneName: 'short',
    hour12: false,
  };

  return dateObj.toLocaleString('sv-SE', {
    ...defaultOpts,
    dateStyle: opts?.dateStyle,
    timeStyle: opts?.timeStyle,
  });
}

/**
 * Format a date/time in UTC with timezone abbreviation
 * @param date - Date string or Date object
 * @param opts - Optional formatting options
 * @returns Formatted string like "2025-11-13 12:48:18 UTC"
 */
export function formatUTC(
  date: string | Date | null | undefined,
  opts?: { 
    dateStyle?: 'full' | 'long' | 'medium' | 'short';
    timeStyle?: 'full' | 'long' | 'medium' | 'short';
    includeSeconds?: boolean;
  }
): string {
  if (!date) return '—';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '—';

  const defaultOpts: Intl.DateTimeFormatOptions = {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: opts?.includeSeconds !== false ? '2-digit' : undefined,
    timeZoneName: 'short',
    hour12: false,
  };

  return dateObj.toLocaleString('sv-SE', {
    ...defaultOpts,
    dateStyle: opts?.dateStyle,
    timeStyle: opts?.timeStyle,
  });
}

/**
 * Format a date/time showing both Stockholm and UTC times
 * @param date - Date string or Date object
 * @returns Formatted string like "2025-11-13 13:48:18 CET (12:48:18 UTC)"
 */
export function formatWithUTC(date: string | Date | null | undefined): string {
  if (!date) return '—';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '—';

  const stockholm = formatStockholm(date);
  const utc = formatUTC(date);
  
  // Extract just the time part from UTC (after the date)
  const utcTimePart = utc.split(' ').slice(1).join(' ');
  
  return `${stockholm} (${utcTimePart})`;
}

/**
 * Format just the date part in Europe/Stockholm timezone
 * @param date - Date string or Date object
 * @returns Formatted string like "2025-11-13"
 */
export function formatDateOnly(date: string | Date | null | undefined): string {
  if (!date) return '—';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '—';

  return dateObj.toLocaleDateString('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Get current server time info for diagnostics
 */
export function getTimeDebugInfo() {
  const now = new Date();
  const offset = -now.getTimezoneOffset() / 60;
  
  return {
    serverNow: now.toISOString(),
    localStockholm: formatStockholm(now),
    localUTC: formatUTC(now),
    browserOffset: offset,
    browserTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
